import { useEffect, useState, useMemo } from "react";
import { Box, Skeleton, useMediaQuery } from "@mui/material";
import pricingmainimage from "../../assets/pricingmainimage.webp";
import CustomText, { textStyles } from "../../components/CustomText";
import { useTheme } from "@mui/material/styles";
import PricingCard from "../../components/cards/PricingCard";
import pricingcard1 from "../../assets/pricingcard1.svg";
import pricingcard2 from "../../assets/pricingcard2.svg";
import pricingcard3 from "../../assets/pricingcard3.svg";
import pricingcard4 from "../../assets/pricingcard4.svg";
import { useTranslation } from "react-i18next";

import { BASE_URL } from "../../components/BASE_URL";

const HeroSection = () => {
  const { t,i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));

  // default should be "monthly"
  const [billing, setBilling] = useState("monthly");
  const [loading, setLoading] = useState(true);
  const [pricingTitle, setPricingTitle] = useState("");
  const [pricingDesc, setPricingDesc] = useState("");

  const [rawPlans, setRawPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [error, setError] = useState(null);

// Fetch section content (title + description)
useEffect(() => {
  setLoading(true);

  fetch(
    `${BASE_URL}/content/sections?page=1&limit=10&sort=order&include=items&search=Pricing`
  )
    .then((res) => res.json())
    .then((json) => {
      const section = json?.data?.[0];
      if (!section) return;

      // 🔑 Pick EN or ES based on current language
      const item =
        i18n.language === "es"
          ? section.items.find((it) => it.idx === 1)
          : section.items.find((it) => it.idx === 0);

      if (item) {
        setPricingTitle(item.title);
        setPricingDesc(item.description);
      } else {
        // fallback to translations
        setPricingTitle(t("new_pricing_hero_title"));
        setPricingDesc(t("new_pricing_hero_subtitle"));
      }
    })
    .catch(() => {
      setPricingTitle(t("new_pricing_hero_title"));
      setPricingDesc(t("new_pricing_hero_subtitle"));
    })
    .finally(() => setLoading(false));
}, [i18n.language, t]); // ✅ re-run when language changes


  // Fetch pricing plans from API
  useEffect(() => {
    async function fetchPlans() {
      try {
        setLoadingPlans(true);
        setError(null);

        const res = await fetch(
          `${BASE_URL}/payments/plans?page=1&limit=105&is_active=true`,
          { method: "GET", headers: { "Content-Type": "application/json" } }
        );

        if (!res.ok) throw new Error(`Failed: ${res.status}`);

        const json = await res.json();
        const raw = Array.isArray(json?.data?.plans) ? json.data.plans : [];
        const active = raw.filter((p) => p?.is_active === true);

        setRawPlans(active);
      } catch (e) {
        setError(e.message || "Error loading plans");
      } finally {
        setLoadingPlans(false);
      }
    }

    fetchPlans();
  }, []);

  const plans = useMemo(() => {
    const lang = i18n.language === "es" ? "es" : "en";

    return rawPlans.map((p, idx) => {
      const localized = p[lang] || p.en || {};

      return {
        key: p.plan_id || String(idx),
        image: [pricingcard1, pricingcard2, pricingcard3, pricingcard3][idx % 4],
        title: localized.name || p.name || "Plan",
        amount: p.amount,
        billing_cycle: p.billing_cycle,
        currency: p.currency || "USD",
        description: localized.description || "",
        features: Array.isArray(localized.features) ? localized.features : [],
        billing_cycle_label: localized.billing_cycle_label || p.billing_cycle,
        billing_cycle_period: localized.billing_cycle_period || p.billing_cycle,
        duration_type_label: localized.duration_type_label || "",
        link: "",
      };
    });
  }, [rawPlans, i18n.language]);

  // filter visible plans based on billing_cycle
  const visiblePlans = useMemo(
    () => plans.filter((p) => p.billing_cycle === billing),
    [plans, billing]
  );

  return (
    <>
      <Box
        sx={{
          position: "relative",
          backgroundImage: `url(${pricingmainimage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          overflow: "visible",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: "rgba(4, 25, 35, 0.65)",
            zIndex: 1,
          }}
        />
        <Box
          sx={{
            position: "relative",
            zIndex: 2,
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "center",
            textAlign: "center",
            color: "#fff",
            px: 2,
            py: isMobile ? 8 : 10,
          }}
        >
          {loading ? (
            <Skeleton height={50} width="60%" />
          ) : (
            <CustomText
              style={{
                ...textStyles.h1,
                color: "#E7EBF0",
                marginBottom: isMobile ? "0rem" : "1rem",
                marginTop: isMobile ? "0rem" : "0rem",
                fontSize: isMobile ? "33px" : "50px",
              }}
            >
              {pricingTitle}
            </CustomText>
          )}

          {loading ? (
            <>
              <Skeleton height={28} width="80%" />
              <Skeleton height={28} width="60%" />
            </>
          ) : (
            <CustomText
              style={{
                ...textStyles.body2,
                color: "#EFEFEF",
                maxWidth: "1175px",
                margin: "0 auto",
                marginTop: isMobile ? "20px" : "6px",
                fontSize: "24px",
              }}
            >
              {pricingDesc}
            </CustomText>
          )}

          {/* Billing Toggle */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: isMobile ? "20px" : "24px",
            }}
          >
            <Box
              display="flex"
              sx={{
                gap: "8px",
                minWidth: isMobile ? "80px" : "140px",
                border: "1px solid #FFFFFF",
                borderRadius: "50px",
              }}
            >
              <Box
                onClick={() => setBilling("monthly")}
                sx={{
                  px: isMobile ? 2 : 4,
                  cursor: "pointer",
                  borderRadius: "48px",
                  backgroundColor: billing === "monthly" ? "#006EC2" : "none",
                  color: "#FFFFFF",
                  transition: "background-color 0.2s, color 0.2s",
                  textAlign: "center",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {t("monthly")}
              </Box>
              <Box
                onClick={() => setBilling("yearly")}
                sx={{
                  px: isMobile ? 2 : 3,
                  py: isMobile ? 0.5 : 1,
                  cursor: "pointer",
                  borderRadius: "50px",
                  backgroundColor: billing === "yearly" ? "#006EC2" : "none",
                  color: "#FFFFFF",
                  transition: "background-color 0.2s, color 0.2s",
                  textAlign: "center",
                  minWidth: isMobile ? "80px" : "100px",
                }}
              >
                {t("yearly")}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Pricing Cards Section */}
      <Box
        component="section"
        sx={{
          position: "relative",
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: isMobile ? 2 : 2,
          px: isMobile ? 2 : 10,
          mt: isMobile ? "-40px" : "-70px",
          zIndex: 3,
          pb: 10,
        }}
      >
        {loadingPlans && <Skeleton height={250} width="80%" />}
        {error && <Box sx={{ color: "red" }}>{error}</Box>}

        {!loadingPlans &&
          visiblePlans.map((plan) => (
            <Box
              key={plan.key}
              sx={{
                flex: isMobile ? "0 0 100%" : "0 0 calc(30% - 32px)",
                maxWidth: isMobile ? "100%" : "calc(30% - 32px)",
              }}
            >
              <PricingCard
                imageSrc={plan.image}
                title={plan.title}
                price={`$${Number(plan.amount).toFixed(2)}`}
                priceLabel={`${plan.currency} / ${t("users")} / ${plan.billing_cycle_period}`}
                description={plan.description}
                yearlyHeading={
                  plan.billing_cycle === "yearly" ? plan.billing_cycle_label : ""
                }
                features={plan.features}
                link={plan.link}
  buttonText={t("subscribe_button")}
              onButtonClick={() => {
  window.location.href = "https://company-admin.biometricpro.app?fromWebsite=true";
}}
                // isHighlighted={plan.highlight}
              />
            </Box>
          ))}
      </Box>
    </>
  );
};

export default HeroSection;











